import { useCallback, useEffect, useRef, useState } from 'react';
import { QuickstartController, QuickstartState, QuickstartPhase } from './QuickstartController';

/**
 * React Hook for Quickstart Flow
 * Manages QuickstartController lifecycle and provides state to components
 */
export function useQuickstartFlow(quickstartSlug: string, debugPhase?: QuickstartPhase) {
  const controllerRef = useRef<QuickstartController | null>(null);
  const [state, setState] = useState<QuickstartState | null>(null);

  useEffect(() => {
    console.log('[useQuickstartFlow] Creating controller for:', quickstartSlug, debugPhase ? `(debug: ${debugPhase})` : '');
    const controller = new QuickstartController(quickstartSlug, debugPhase);
    controllerRef.current = controller;

    const listenerId = controller.getState().addListener((newState) => {
      setState(newState);
    });

    setState(controller.getState().currentState);
    controller.init();

    return () => {
      console.log('[useQuickstartFlow] Cleaning up controller');
      controller.getState().removeListener(listenerId);
      controller.cleanup();
      controllerRef.current = null;
    };
  }, [quickstartSlug, debugPhase]);

  // All callbacks use controllerRef (a stable ref) so they never need to change identity.
  // This prevents useEffect churn in consuming components like PlayerWithQuickstart.

  const onFirstInput = useCallback(() => controllerRef.current?.onFirstInput(), []);
  const onReturnToPlayNow = useCallback(() => controllerRef.current?.onReturnToPlayNow(), []);
  const onWishlistClicked = useCallback(() => controllerRef.current?.onWishlistClicked(), []);
  const onWishlistCTAClicked = useCallback(
    (triggerSource?: 'cta_page' | 'thank_you' | 'matchmaking_failed' | 'broadcast_join_failed' | 'error' | 'auth_failed' | 'auto_redirect' | 'afk_timer_hit' | 'maxed_out_game_time' | 'loading_timeout') =>
      controllerRef.current?.onWishlistCTAClicked(triggerSource),
    [],
  );
  const onWishlistEmailSend = useCallback(
    (playerAuthId: string, email: string, persistentId: string) =>
      controllerRef.current?.onWishlistEmailSend(playerAuthId, email, persistentId),
    [],
  );
  const onLoadingTimeout = useCallback(() => controllerRef.current?.onLoadingTimeout(), []);
  const onDeveloperLogoClicked = useCallback((url: string) => controllerRef.current?.onDeveloperLogoClicked(url), []);
  const onThankYouRedirect = useCallback((steamUrl: string) => controllerRef.current?.onThankYouRedirect(steamUrl), []);
  const onOpenInBrowserClicked = useCallback(() => controllerRef.current?.onOpenInBrowserClicked(), []);
  const onAuthSuccess = useCallback(
    (playerAuthId: string, email?: string, persistentId?: string) =>
      controllerRef.current?.onAuthSuccess(playerAuthId, email, persistentId),
    [],
  );
  const onAuthFailure = useCallback((reason: string) => controllerRef.current?.onAuthFailure(reason), []);
  const onClerkUnavailable = useCallback(() => controllerRef.current?.onClerkUnavailable(), []);
  const retryMatchmaking = useCallback(() => controllerRef.current?.retryMatchmaking(), []);
  const requestBrowserLocks = useCallback(async () => {
    if (controllerRef.current) {
      await controllerRef.current.requestBrowserLocks();
    }
  }, []);

  const onAfkTimeout = useCallback(() => controllerRef.current?.onAfkTimeout(), []);
  const onAdTimerExpired = useCallback(() => controllerRef.current?.onAdTimerExpired(), []);
  const onPlayerDisconnect = useCallback(() => controllerRef.current?.onPlayerDisconnect(true), []);

  const getAFKRemainingTime = useCallback(() => controllerRef.current?.getAFKRemainingTime() ?? 0, []);
  const getAdRemainingTime = useCallback(() => controllerRef.current?.getAdRemainingTime() ?? 0, []);

  const resetInGameIdleTimer = useCallback(() => controllerRef.current?.resetInGameIdleTimer(), []);
  const getInGameIdleRemainingTime = useCallback(() => controllerRef.current?.getInGameIdleRemainingTime() ?? 0, []);
  const forceInGameIdle = useCallback(() => controllerRef.current?.forceInGameIdle(), []);

  const debugJumpToPhase = useCallback((phase: QuickstartPhase) => controllerRef.current?.debugJumpToPhase(phase), []);
  const debugSetReleaseTimestamp = useCallback(
    (timestamp: number | 'past' | 'future') => controllerRef.current?.debugSetReleaseTimestamp(timestamp),
    [],
  );

  return {
    state,
    controller: controllerRef.current,
    onFirstInput,
    onReturnToPlayNow,
    onWishlistClicked,
    onWishlistCTAClicked,
    onWishlistEmailSend,
    onLoadingTimeout,
    onDeveloperLogoClicked,
    onThankYouRedirect,
    onOpenInBrowserClicked,
    onAuthSuccess,
    onAuthFailure,
    onClerkUnavailable,
    retryMatchmaking,
    requestBrowserLocks,
    onAfkTimeout,
    onAdTimerExpired,
    onPlayerDisconnect,
    getAFKRemainingTime,
    getAdRemainingTime,
    resetInGameIdleTimer,
    getInGameIdleRemainingTime,
    forceInGameIdle,
    debugJumpToPhase,
    debugSetReleaseTimestamp,
  };
}

